import React, { useCallback, useEffect, useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/Dialog'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'

interface NewPlaybookDialogProps {
	isOpen: boolean
	onClose: () => void
	onCreatePlaybook: (name: string) => Promise<void>
}

export function NewPlaybookDialog({
	isOpen,
	onClose,
	onCreatePlaybook,
}: NewPlaybookDialogProps) {
	const [name, setName] = useState('')
	const [isCreating, setIsCreating] = useState(false)

	// Reset form when dialog opens
	useEffect(() => {
		if (isOpen) {
			setName('')
			setIsCreating(false)
		}
	}, [isOpen])

	const handleCreate = useCallback(async () => {
		const trimmedName = name.trim()
		if (!trimmedName || isCreating) return

		setIsCreating(true)
		try {
			await onCreatePlaybook(trimmedName)
			setName('')
			onClose()
		} catch (error) {
			console.error('Failed to create playbook:', error)
		} finally {
			setIsCreating(false)
		}
	}, [name, isCreating, onCreatePlaybook, onClose])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && name.trim() && !isCreating) {
				handleCreate()
			}
		},
		[handleCreate, name, isCreating],
	)

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open && !isCreating) {
				setName('')
				onClose()
			}
		},
		[onClose, isCreating],
	)

	const isFormValid = name.trim().length > 0

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create New Playbook</DialogTitle>
				</DialogHeader>
				<div className="form-field">
					<label className="form-label">Playbook Name</label>
					<Input
						type="text"
						value={name}
						onChange={e => setName(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder="Enter playbook name..."
						disabled={isCreating}
						autoFocus
					/>
				</div>
				<DialogFooter>
					<Button
						variant="ghost"
						onClick={onClose}
						disabled={isCreating}
					>
						Cancel
					</Button>
					<Button
						variant="action"
						onClick={handleCreate}
						disabled={!isFormValid || isCreating}
					>
						{isCreating ? 'Creating...' : 'Create'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
